import { inject } from '@angular/core';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { forkJoin, of, pipe, tap } from 'rxjs';
import { catchError, exhaustMap, finalize } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { IOrganizationState } from './organization.state';
import { withOrganizationStateMethods } from './organization.methods.state';
import { OrganizationService } from '../../services/organization/organization.service';
import { NotificationService } from '../../services/notification/notification.service';
import { GlobalSpinnerService } from '../../services/global-spinner/global-spinner.service';
import { Client, CreateClient, EditClient } from '../../models/organization/client.model';
import {
  ClientBillingProfile,
  UpsertClientBillingProfile,
} from '../../models/organization/client-billing-profile.model';
import {
  ClientBillingRun,
  ClientBillingRunListItem,
  CreateClientBillingRun,
  UpdateClientBillingLine,
  UpsertClientBillingAdditionalLine,
} from '../../models/organization/client-billing-run.model';
import {
  BillingItemType,
  CreateBillingItemType,
  EditBillingItemType,
} from '../../models/organization/billing-item-type.model';
import {
  BillingSssBracket,
  ReplaceBillingSssBrackets,
} from '../../models/organization/billing-sss-bracket.model';
import { Deployment, CreateDeployment, EditDeployment } from '../../models/organization/deployment.model';
import { Department, CreateDepartment, EditDepartment } from '../../models/organization/department.model';
import { Branch, CreateBranch, EditBranch } from '../../models/organization/branch.model';
import { Position, CreatePosition, EditPosition } from '../../models/organization/position.model';
import { WorkSchedule, CreateWorkSchedule, EditWorkSchedule } from '../../models/organization/work-schedule.model';

function billingSssBracketList(
  response: { items?: BillingSssBracket[] } | BillingSssBracket[] | null | undefined,
): BillingSssBracket[] {
  if (Array.isArray(response)) return response;
  return response?.items ?? [];
}

export const withOrganizationApiMethods = signalStoreFeature(
  { state: type<IOrganizationState>() },
  withOrganizationStateMethods,
  withMethods((store) => {
    const orgService = inject(OrganizationService);
    const notificationService = inject(NotificationService);
    const spinner = inject(GlobalSpinnerService);

    const loadOrganizationData = rxMethod<void>(
      pipe(
        tap(() => {
          patchState(store, { loading: true, error: null });
        }),
        exhaustMap(() =>
          forkJoin({
            departments: orgService.getDepartments().pipe(
              catchError(() => of({ departments: [] as Department[] })),
            ),
            positions: orgService.getPositions().pipe(
              catchError(() => of({ positions: [] as Position[] })),
            ),
            workSchedules: orgService.getWorkSchedules().pipe(
              catchError(() => of({ workSchedules: [] as WorkSchedule[] })),
            ),
          }).pipe(
            tapResponse({
              next: (results) => {
                store.setDepartments(results.departments.departments ?? []);
                store.setPositions(results.positions.positions ?? []);
                store.setWorkSchedules(results.workSchedules.workSchedules ?? []);
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
              },
            }),
            finalize(() => {
              patchState(store, { loading: false });
              spinner.hide();
            }),
          )
        )
      )
    );

    const loadClients = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(() =>
          orgService.getClients().pipe(
            tapResponse({
              next: (response: { clients: Client[] }) => {
                store.setClients(response.clients);
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )
    );

    const loadDeployments = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(() =>
          orgService.getDeployments().pipe(
            tapResponse({
              next: (response: { deployments: Deployment[] }) => {
                store.setDeployments(response.deployments);
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )
    );

    const loadBillingRuns = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(() =>
          orgService.getClientBillingRuns().pipe(
            tapResponse({
              next: (response: { runs: ClientBillingRunListItem[] }) => {
                store.setBillingRuns(response.runs);
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )
    );

    const loadDepartments = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(() =>
          orgService.getDepartments().pipe(
            tapResponse({
              next: (response: { departments: Department[] }) => {
                store.setDepartments(response.departments);
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )
    );

    const loadBranches = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(() =>
          orgService.getBranches().pipe(
            tapResponse({
              next: (response: { branches: Branch[] }) => {
                store.setBranches(response.branches);
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )
    );

    const loadPositions = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(() =>
          orgService.getPositions().pipe(
            tapResponse({
              next: (response: { positions: Position[] }) => {
                store.setPositions(response.positions);
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )
    );

    const loadPositionsByDepartment = rxMethod<string>(
      pipe(
        exhaustMap((departmentGuid) =>
          orgService.getPositionsByDepartment(departmentGuid).pipe(
            tapResponse({
              next: (response: { positions: Position[] }) => {
                patchState(store, { filteredPositions: response.positions });
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )
    );

    const loadWorkSchedules = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(() =>
          orgService.getWorkSchedules().pipe(
            tapResponse({
              next: (response: { workSchedules: WorkSchedule[] }) => {
                store.setWorkSchedules(response.workSchedules);
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )
    );

    return {
      loadOrganizationData,
      loadClients,
      loadDeployments,
      loadBillingRuns,
      loadDepartments,
      loadBranches,
      loadPositions,
      loadPositionsByDepartment,
      loadWorkSchedules,
      createClient: rxMethod<{ payload: CreateClient; onSuccess?: () => void }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ payload, onSuccess }) =>
          orgService.createClient(payload).pipe(
            tapResponse({
              next: (response: { client: Client }) => {
                store.addClient(response.client);
                notificationService.notify('success', 'Created', 'Client created.');
                onSuccess?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )),
      editClient: rxMethod<{ guid: string; payload: EditClient; onSuccess?: () => void }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, payload, onSuccess }) =>
          orgService.editClient(guid, payload).pipe(
            tapResponse({
              next: (response: { client: Client }) => {
                store.updateClient(response.client);
                notificationService.notify('success', 'Updated', 'Client updated.');
                onSuccess?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )),
      deleteClient: rxMethod<{ guid: string; onSuccess?: () => void }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, onSuccess }) =>
          orgService.deleteClient(guid).pipe(
            tapResponse({
              next: () => {
                store.removeClient(guid);
                notificationService.notify('success', 'Deleted', 'Client deleted.');
                onSuccess?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )),
      loadClientBillingProfile: rxMethod<{
        guid: string;
        onSuccess?: (profile: ClientBillingProfile) => void;
        onDone?: () => void;
      }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, onSuccess, onDone }) =>
          orgService.getClientBillingProfile(guid).pipe(
            tapResponse({
              next: (response: { profile: ClientBillingProfile | null }) => {
                patchState(store, { loading: false, error: null });
                if (response.profile) onSuccess?.(response.profile);
                onDone?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
                onDone?.();
              },
            })
          )
        )
      )),
      upsertClientBillingProfile: rxMethod<{
        guid: string;
        payload: UpsertClientBillingProfile;
        onSuccess?: (profile: ClientBillingProfile) => void;
        onDone?: () => void;
      }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, payload, onSuccess, onDone }) =>
          orgService.upsertClientBillingProfile(guid, payload).pipe(
            tapResponse({
              next: (response: { profile: ClientBillingProfile }) => {
                patchState(store, { loading: false, error: null });
                notificationService.notify('success', 'Saved', 'Billing profile saved.');
                onSuccess?.(response.profile);
                onDone?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
                onDone?.();
              },
            })
          )
        )
      )),
      loadBillingRunByGuid: rxMethod<{
        guid: string;
        onSuccess?: (run: ClientBillingRun) => void;
        onDone?: () => void;
      }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, onSuccess, onDone }) =>
          orgService.getClientBillingRunByGuid(guid).pipe(
            tapResponse({
              next: (response: { run: ClientBillingRun | null }) => {
                store.setSelectedBillingRun(response.run);
                if (response.run) onSuccess?.(response.run);
                onDone?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
                onDone?.();
              },
            })
          )
        )
      )),
      createBillingRun: rxMethod<{
        payload: CreateClientBillingRun;
        onSuccess?: (run: ClientBillingRun) => void;
        onError?: (message: string) => void;
        onDone?: () => void;
      }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ payload, onSuccess, onError, onDone }) =>
          orgService.createClientBillingRun(payload).pipe(
            tapResponse({
              next: (response: { run: ClientBillingRun }) => {
                store.setSelectedBillingRun(response.run);
                store.setBillingRuns([
                  {
                    clientBillingRunGuid: response.run.clientBillingRunGuid,
                    clientGuid: response.run.clientGuid,
                    clientName: response.run.clientName,
                    runNumber: response.run.runNumber,
                    periodStart: response.run.periodStart,
                    periodEnd: response.run.periodEnd,
                    status: response.run.status,
                    totalEmployees: response.run.totalEmployees,
                    totalGrossTotal: response.run.totalGrossTotal,
                    totalAdminFee: response.run.totalAdminFee,
                    totalAmountDue: response.run.totalAmountDue,
                    completedAt: response.run.completedAt,
                    createdOn: response.run.createdOn,
                  },
                  ...store.billingRuns(),
                ]);
                notificationService.notify('success', 'Created', 'Billing run created.');
                onSuccess?.(response.run);
                onDone?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                onError?.(message);
                onDone?.();
              },
            })
          )
        )
      )),
      processBillingRun: rxMethod<{
        guid: string;
        onSuccess?: (run: ClientBillingRun) => void;
        onDone?: () => void;
      }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, onSuccess, onDone }) =>
          orgService.processClientBillingRun(guid).pipe(
            tapResponse({
              next: (response: { run: ClientBillingRun }) => {
                store.setSelectedBillingRun(response.run);
                store.setBillingRuns(
                  store.billingRuns().map((r) =>
                    r.clientBillingRunGuid === response.run.clientBillingRunGuid
                      ? {
                          ...r,
                          status: response.run.status,
                          totalEmployees: response.run.totalEmployees,
                          totalGrossTotal: response.run.totalGrossTotal,
                          totalAdminFee: response.run.totalAdminFee,
                          totalAmountDue: response.run.totalAmountDue,
                          completedAt: response.run.completedAt,
                        }
                      : r
                  )
                );
                notificationService.notify('success', 'Processed', 'Billing run calculated.');
                onSuccess?.(response.run);
                onDone?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
                onDone?.();
              },
            })
          )
        )
      )),
      updateBillingRunSssEcOverride: rxMethod<{
        guid: string;
        sssEcModeOverride: number | null;
        includePhicErOverride: boolean | null;
        includeHdmfErOverride: boolean | null;
        onSuccess?: (run: ClientBillingRun) => void;
        onDone?: () => void;
      }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, sssEcModeOverride, includePhicErOverride, includeHdmfErOverride, onSuccess, onDone }) =>
          orgService.updateClientBillingSssEcOverride(guid, {
            sssEcModeOverride,
            includePhicErOverride,
            includeHdmfErOverride,
          }).pipe(
            tapResponse({
              next: (response: { run: ClientBillingRun }) => {
                store.setSelectedBillingRun(response.run);
                patchState(store, { loading: false, error: null });
                notificationService.notify('success', 'Saved', 'Run billing overrides updated.');
                onSuccess?.(response.run);
                onDone?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
                onDone?.();
              },
            })
          )
        )
      )),
      importBillingHoursFromDtr: rxMethod<{
        guid: string;
        onSuccess?: (run: ClientBillingRun) => void;
        onDone?: () => void;
      }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, onSuccess, onDone }) =>
          orgService.importClientBillingHoursFromDtr(guid).pipe(
            tapResponse({
              next: (response: { run: ClientBillingRun }) => {
                store.setSelectedBillingRun(response.run);
                patchState(store, { loading: false, error: null });
                notificationService.notify('success', 'Imported', 'Regular and OT hours updated from DTR.');
                onSuccess?.(response.run);
                onDone?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
                onDone?.();
              },
            })
          )
        )
      )),
      importBillingFromPayroll: rxMethod<{
        guid: string;
        onSuccess?: (run: ClientBillingRun) => void;
        onDone?: () => void;
      }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, onSuccess, onDone }) =>
          orgService.importClientBillingFromPayroll(guid).pipe(
            tapResponse({
              next: (response: { run: ClientBillingRun }) => {
                store.setSelectedBillingRun(response.run);
                patchState(store, { loading: false, error: null });
                notificationService.notify(
                  'success',
                  'Imported',
                  'Gross pay and employer contributions copied from completed payroll. Edit lines if needed, then Process.'
                );
                onSuccess?.(response.run);
                onDone?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
                onDone?.();
              },
            })
          )
        )
      )),
      updateBillingLine: rxMethod<{
        runGuid: string;
        lineGuid: string;
        payload: UpdateClientBillingLine;
        onSuccess?: () => void;
        onDone?: () => void;
      }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ runGuid, lineGuid, payload, onSuccess, onDone }) =>
          orgService.updateClientBillingLine(runGuid, lineGuid, payload).pipe(
            tapResponse({
              next: () => {
                patchState(store, { loading: false, error: null });
                notificationService.notify('success', 'Updated', 'Line updated.');
                onSuccess?.();
                onDone?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
                onDone?.();
              },
            })
          )
        )
      )),
      deleteBillingRun: rxMethod<{ guid: string; onSuccess?: () => void }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, onSuccess }) =>
          orgService.deleteClientBillingRun(guid).pipe(
            tapResponse({
              next: () => {
                store.removeBillingRun(guid);
                notificationService.notify('success', 'Deleted', 'Billing run deleted.');
                onSuccess?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )),
      addBillingAdditionalLine: rxMethod<{
        runGuid: string;
        payload: UpsertClientBillingAdditionalLine;
        onSuccess?: () => void;
        onDone?: () => void;
      }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ runGuid, payload, onSuccess, onDone }) =>
          orgService.addClientBillingAdditionalLine(runGuid, payload).pipe(
            tapResponse({
              next: (response: { run: ClientBillingRun }) => {
                store.setSelectedBillingRun(response.run);
                patchState(store, { loading: false, error: null });
                notificationService.notify('success', 'Added', 'Additional item added.');
                onSuccess?.();
                onDone?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
                onDone?.();
              },
            })
          )
        )
      )),
      updateBillingAdditionalLine: rxMethod<{
        runGuid: string;
        lineGuid: string;
        payload: UpsertClientBillingAdditionalLine;
        onSuccess?: () => void;
        onDone?: () => void;
      }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ runGuid, lineGuid, payload, onSuccess, onDone }) =>
          orgService.updateClientBillingAdditionalLine(runGuid, lineGuid, payload).pipe(
            tapResponse({
              next: (response: { run: ClientBillingRun }) => {
                store.setSelectedBillingRun(response.run);
                patchState(store, { loading: false, error: null });
                notificationService.notify('success', 'Updated', 'Additional item updated.');
                onSuccess?.();
                onDone?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
                onDone?.();
              },
            })
          )
        )
      )),
      deleteBillingAdditionalLine: rxMethod<{
        runGuid: string;
        lineGuid: string;
        onSuccess?: () => void;
        onDone?: () => void;
      }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ runGuid, lineGuid, onSuccess, onDone }) =>
          orgService.deleteClientBillingAdditionalLine(runGuid, lineGuid).pipe(
            tapResponse({
              next: (response: { run: ClientBillingRun }) => {
                store.setSelectedBillingRun(response.run);
                patchState(store, { loading: false, error: null });
                notificationService.notify('success', 'Deleted', 'Additional item removed.');
                onSuccess?.();
                onDone?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
                onDone?.();
              },
            })
          )
        )
      )),
      loadBillingItemTypes: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          exhaustMap(() =>
            orgService.getBillingItemTypes().pipe(
              tapResponse({
                next: (response: { items: BillingItemType[] }) => {
                  store.setBillingItemTypes(response.items);
                },
                error: (error: HttpErrorResponse) => {
                  store.setError(error.error?.message ?? error.message);
                },
              })
            )
          )
        )
      ),
      createBillingItemType: rxMethod<{
        payload: CreateBillingItemType;
        onSuccess?: () => void;
        onDone?: () => void;
      }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ payload, onSuccess, onDone }) =>
          orgService.createBillingItemType(payload).pipe(
            tapResponse({
              next: (response: { item: BillingItemType }) => {
                store.addBillingItemType(response.item);
                notificationService.notify('success', 'Created', 'Billing item created.');
                onSuccess?.();
                onDone?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
                onDone?.();
              },
            })
          )
        )
      )),
      editBillingItemType: rxMethod<{
        guid: string;
        payload: EditBillingItemType;
        onSuccess?: () => void;
        onDone?: () => void;
      }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, payload, onSuccess, onDone }) =>
          orgService.editBillingItemType(guid, payload).pipe(
            tapResponse({
              next: (response: { item: BillingItemType }) => {
                store.updateBillingItemType(response.item);
                notificationService.notify('success', 'Updated', 'Billing item updated.');
                onSuccess?.();
                onDone?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
                onDone?.();
              },
            })
          )
        )
      )),
      deleteBillingItemType: rxMethod<{ guid: string; onSuccess?: () => void }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, onSuccess }) =>
          orgService.deleteBillingItemType(guid).pipe(
            tapResponse({
              next: () => {
                store.removeBillingItemType(guid);
                notificationService.notify('success', 'Deleted', 'Billing item deleted.');
                onSuccess?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )),
      loadBillingSssBrackets: rxMethod<{
        onDone?: () => void;
      } | void>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          exhaustMap((args) =>
            orgService.getBillingSssBrackets().pipe(
              tapResponse({
                next: (response: { items?: BillingSssBracket[] } | BillingSssBracket[]) => {
                  store.setBillingSssBrackets(billingSssBracketList(response));
                  (args as { onDone?: () => void } | void)?.onDone?.();
                },
                error: (error: HttpErrorResponse) => {
                  store.setError(error.error?.message ?? error.message);
                  (args as { onDone?: () => void } | void)?.onDone?.();
                },
              })
            )
          )
        )
      ),
      replaceBillingSssBrackets: rxMethod<{
        payload: ReplaceBillingSssBrackets;
        onSuccess?: () => void;
        onDone?: () => void;
      }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ payload, onSuccess, onDone }) =>
          orgService.replaceBillingSssBrackets(payload).pipe(
            tapResponse({
              next: (response: { items?: BillingSssBracket[] } | BillingSssBracket[]) => {
                store.setBillingSssBrackets(billingSssBracketList(response));
                notificationService.notify('success', 'Saved', 'SSS billing table updated.');
                onSuccess?.();
                onDone?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
                onDone?.();
              },
            })
          )
        )
      )),
      resetBillingSssBrackets: rxMethod<{
        onSuccess?: () => void;
        onDone?: () => void;
      }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ onSuccess, onDone }) =>
          orgService.resetBillingSssBrackets().pipe(
            tapResponse({
              next: (response: { items?: BillingSssBracket[] } | BillingSssBracket[]) => {
                store.setBillingSssBrackets(billingSssBracketList(response));
                notificationService.notify('success', 'Reset', 'SSS billing table restored to defaults.');
                onSuccess?.();
                onDone?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
                onDone?.();
              },
            })
          )
        )
      )),
      createDeployment: rxMethod<{ payload: CreateDeployment; onSuccess?: () => void; onDone?: () => void }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ payload, onSuccess, onDone }) =>
          orgService.createDeployment(payload).pipe(
            tapResponse({
              next: (response: { deployment: Deployment }) => {
                store.addDeployment(response.deployment);
                notificationService.notify('success', 'Created', 'Deployment created.');
                onSuccess?.();
                onDone?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
                onDone?.();
              },
            })
          )
        )
      )),
      editDeployment: rxMethod<{ guid: string; payload: EditDeployment; onSuccess?: () => void; onDone?: () => void }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, payload, onSuccess, onDone }) =>
          orgService.editDeployment(guid, payload).pipe(
            tapResponse({
              next: (response: { deployment: Deployment }) => {
                store.updateDeployment(response.deployment);
                notificationService.notify('success', 'Updated', 'Deployment updated.');
                onSuccess?.();
                onDone?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
                onDone?.();
              },
            })
          )
        )
      )),
      deleteDeployment: rxMethod<{ guid: string; onSuccess?: () => void }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, onSuccess }) =>
          orgService.deleteDeployment(guid).pipe(
            tapResponse({
              next: () => {
                store.removeDeployment(guid);
                notificationService.notify('success', 'Deleted', 'Deployment deleted.');
                onSuccess?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )),
      createWorkSchedule: rxMethod<{ payload: CreateWorkSchedule; onSuccess?: () => void }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ payload, onSuccess }) =>
          orgService.createWorkSchedule(payload).pipe(
            tapResponse({
              next: (response: { workSchedule: WorkSchedule }) => {
                store.addWorkSchedule(response.workSchedule);
                notificationService.notify('success', 'Created', 'Work schedule created.');
                onSuccess?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )),
      editWorkSchedule: rxMethod<{ guid: string; payload: EditWorkSchedule; onSuccess?: () => void }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, payload, onSuccess }) =>
          orgService.editWorkSchedule(guid, payload).pipe(
            tapResponse({
              next: (response: { workSchedule: WorkSchedule }) => {
                store.updateWorkSchedule(response.workSchedule);
                notificationService.notify('success', 'Updated', 'Work schedule updated.');
                onSuccess?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )),
      deleteWorkSchedule: rxMethod<{ guid: string; onSuccess?: () => void }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, onSuccess }) =>
          orgService.deleteWorkSchedule(guid).pipe(
            tapResponse({
              next: () => {
                store.removeWorkSchedule(guid);
                notificationService.notify('success', 'Deleted', 'Work schedule deleted.');
                onSuccess?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )),
      createDepartment: rxMethod<{ payload: CreateDepartment; onSuccess?: () => void }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ payload, onSuccess }) =>
          orgService.createDepartment(payload).pipe(
            tapResponse({
              next: (response: { department: Department }) => {
                store.addDepartment(response.department);
                notificationService.notify('success', 'Created', 'Department created.');
                onSuccess?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )),
      editDepartment: rxMethod<{ guid: string; payload: EditDepartment; onSuccess?: () => void }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, payload, onSuccess }) =>
          orgService.editDepartment(guid, payload).pipe(
            tapResponse({
              next: (response: { department: Department }) => {
                store.updateDepartment(response.department);
                notificationService.notify('success', 'Updated', 'Department updated.');
                onSuccess?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )),
      deleteDepartment: rxMethod<{ guid: string; onSuccess?: () => void }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, onSuccess }) =>
          orgService.deleteDepartment(guid).pipe(
            tapResponse({
              next: () => {
                store.removeDepartment(guid);
                notificationService.notify('success', 'Deleted', 'Department deleted.');
                onSuccess?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )),
      createBranch: rxMethod<{ payload: CreateBranch; onSuccess?: () => void }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ payload, onSuccess }) =>
          orgService.createBranch(payload).pipe(
            tapResponse({
              next: (response: { branch: Branch }) => {
                store.addBranch(response.branch);
                notificationService.notify('success', 'Created', 'Branch created.');
                onSuccess?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )),
      editBranch: rxMethod<{ guid: string; payload: EditBranch; onSuccess?: () => void }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, payload, onSuccess }) =>
          orgService.editBranch(guid, payload).pipe(
            tapResponse({
              next: (response: { branch: Branch }) => {
                store.updateBranch(response.branch);
                notificationService.notify('success', 'Updated', 'Branch updated.');
                onSuccess?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )),
      deleteBranch: rxMethod<{ guid: string; onSuccess?: () => void }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, onSuccess }) =>
          orgService.deleteBranch(guid).pipe(
            tapResponse({
              next: () => {
                store.removeBranch(guid);
                notificationService.notify('success', 'Deleted', 'Branch deleted.');
                onSuccess?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )),
      createPosition: rxMethod<{ payload: CreatePosition; onSuccess?: () => void }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ payload, onSuccess }) =>
          orgService.createPosition(payload).pipe(
            tapResponse({
              next: (response: { position: Position }) => {
                store.addPosition(response.position);
                notificationService.notify('success', 'Created', 'Position created.');
                onSuccess?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )),
      editPosition: rxMethod<{ guid: string; payload: EditPosition; onSuccess?: () => void }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, payload, onSuccess }) =>
          orgService.editPosition(guid, payload).pipe(
            tapResponse({
              next: (response: { position: Position }) => {
                store.updatePosition(response.position);
                notificationService.notify('success', 'Updated', 'Position updated.');
                onSuccess?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )),
      deletePosition: rxMethod<{ guid: string; onSuccess?: () => void }>(pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap(({ guid, onSuccess }) =>
          orgService.deletePosition(guid).pipe(
            tapResponse({
              next: () => {
                store.removePosition(guid);
                notificationService.notify('success', 'Deleted', 'Position deleted.');
                onSuccess?.();
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )),
    };
  })
);
